<template>
    <div class="h-full w-[90%] lg:w-1/2 flex justify-center items-center">
        <div class="relative w-full lg:w-auto bg-white rounded-3xl shadow">



            <!-- Header -->
            <div :class="[
                'text-white w-full py-5 text-lg bg-gradient-to-br from-gifty-pink to-gifty-pink_dark rounded-t-3xl flex justify-center items-center text-center font-extrabold',
                locale === 'ar' ? 'font-arabic' : ''
            ]">
                {{ $t('register.verification.verification_title') }}
            </div>

            <!-- Body -->
            <div class="w-full px-3 py-5 grid grid-cols-1 gap-5"> <!-- Stepper -->
                <Stepper_container /> <!-- Phone input -->
                <div class="w-full">
                    <div class="w-1/3 mx-auto py-5">
                        <lock_icon />
                    </div>
                    <div
                        :class="['text-lg font-medium text-gifty-text_blue text-center px-5', locale === 'ar' ? 'font-arabic' : '']">
                        {{ $t('register.verification.description') }} </div>
                </div>
                <div class="flex justify-center gap-3" dir="ltr"> <input v-for="(digit, index) in otp" :key="index"
                        ref="otpInputs" v-model="otp[index]" @input="handleInput(index)"
                        @paste="index === 0 ? handlePaste($event) : null" @keydown.backspace="handleBackspace(index)"
                        type="text" inputmode="numeric"
                        :class="['w-10 aspect-square lg:w-12 lg:h-12 text-center text-xl border rounded-lg focus:border-[#b63753] focus:border-2 outline-none', locale === 'ar' ? 'font-arabic' : '', !codeError ? 'border-gray-300' : 'border-red-500']" />
                </div>
                <p v-if="codeError"
                    :class="['text-sm text-red-500 mt-1 text-center font-medium', locale === 'ar' ? 'font-arabic' : '']">
                    {{ $t('register.errors.code') }} </p>
                <div v-if="countdown === 0"
                    class="flex justify-center items-center space-x-2 text-sm text-center w-full">
                    <div :class="locale === 'ar' ? 'font-arabic' : ''"> {{
                        $t('register.verification.didnt_get_code') }} </div>
                    <div @click="resendCode"
                        :class="['underline text-[#b63753] hover:cursor-pointer font-medium', locale === 'ar' ? 'font-arabic' : '']">
                        {{ $t('register.verification.resend') }} </div>
                </div>
                <div v-else class="text-gray-500 font-medium mx-auto text-center text-sm">
                    <div :class="locale === 'ar' ? 'font-arabic' : ''"> {{ $t('register.verification.resendIn', {
                        seconds: countdown
                    }) }} </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import lock_icon from '@/components/icons/lock_icon.vue'
import Stepper_container from '@/components/stepper_container.vue';
import { useRegistrationStore } from '@/stores/registrationStore'
import { ref, onMounted, watch, defineEmits, nextTick } from 'vue'
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n'
const { locale } = useI18n()


const registerStore = useRegistrationStore();

const otp = ref(['', '', '', '', '', ''])
const otpInputs = ref([])
const codeError = ref(false);
const router = useRouter()

onMounted(() => {
    otpInputs.value[0]?.focus()


    otpInputs.value[0]?.addEventListener('focus', async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.readText) {
                const clipboardText = await navigator.clipboard.readText()
                if (/^\d{4,6}$/.test(clipboardText)) {
                    pasteOtp(clipboardText)
                }
            }
        } catch (err) {
            // silent fail if no permissions
        }
    })
    startCountdown();
})


const handleInput = (index) => {
    // Strip any non-digit characters
    otp.value[index] = otp.value[index].replace(/\D/g, '')

    // If the user pasted or the keyboard inserted multiple digits at once
    if (otp.value[index].length > 1) {
        const digits = otp.value[index].split('')
        digits.forEach((digit, i) => {
            if (index + i < otp.value.length) {
                otp.value[index + i] = digit
            }
        })
        // Focus the last filled input
        const lastIndex = Math.min(index + digits.length, otp.value.length - 1)
        otpInputs.value[lastIndex]?.focus()
        return
    }

    // Normal single-digit case
    if (otp.value[index] && index < otp.value.length - 1) {
        otpInputs.value[index + 1]?.focus()
    }
}

const handleBackspace = (index) => {
    if (!otp.value[index] && index > 0) {
        otpInputs.value[index - 1]?.focus()
    }
}

watch(otp, (val) => {
    if (val.every((digit) => digit !== '')) {
        submitOtp()
    }
}, { deep: true })


const submitOtp = async () => {

    codeError.value = false;
    emit('toggleLoading', true);

    let otpValue = otp.value.join("");
    let response = await registerStore.checkOtpRequest(otpValue);


    if (response.success) {
        emit('toggleMessageModal', true, true, "phone_verification_success", "phone");
        registerStore.goToForm()
        router.push({ name: 'form' });
    } else {
        codeError.value = true;
    }

    emit('toggleLoading', false);
}


const countdown = ref(0)
let timer = null

function startCountdown() {
    countdown.value = 60
    clearInterval(timer)
    timer = setInterval(() => {
        if (countdown.value > 0) countdown.value--
        else clearInterval(timer)
    }, 1000)
}

const resendCode = async () => {

    let response = await registerStore.sendOtpRequest(registerStore.phone);
    if (response.success) {
        startCountdown()
    }
}


const emit = defineEmits([
    'toggleLoading', 'toggleMessageModal'
])

const handlePaste = (event) => {
    const pastedData = event.clipboardData.getData('text').trim()
    if (!/^\d+$/.test(pastedData)) return
    pasteOtp(pastedData)
}


function pasteOtp(data) {
    data.split('').forEach((digit, index) => {
        if (index < otp.value.length) {
            otp.value[index] = digit
        }
    })
    nextTick(() => {
        const lastIndex = Math.min(data.length, otp.value.length) - 1
        otpInputs.value[lastIndex]?.focus()
    })
}
</script>

<style scoped>
.loader {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>